import { useEffect, useState } from 'react';
import { History, ChevronDown, ChevronRight, Calendar } from 'lucide-react';
import { apiGet } from '../lib/api';

interface MeetingInstance {
  id: string;
  title: string | null;
  meeting_date: string;
  summary: string | null;
}

interface MeetingHistorySectionProps {
  seriesId: string;
  currentInstanceId: string;
  onOpenMeeting: (instanceId: string) => void;
  activeDrawerId: string | null;
}

export function MeetingHistorySection({
  seriesId,
  currentInstanceId,
  onOpenMeeting,
  activeDrawerId,
}: MeetingHistorySectionProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [instances, setInstances] = useState<MeetingInstance[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    apiGet<MeetingInstance[]>(`/meeting-series/${seriesId}/instances`)
      .then((data) => {
        if (!cancelled) {
          // Sort newest first
          const sorted = [...data].sort(
            (a, b) => new Date(b.meeting_date).getTime() - new Date(a.meeting_date).getTime(),
          );
          setInstances(sorted);
        }
      })
      .catch(console.error)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [seriesId]);

  return (
    <div className="border-b border-border pb-3 mb-3 last:border-b-0 last:pb-0 last:mb-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 w-full text-left py-1.5 text-xs font-semibold text-text-secondary uppercase tracking-wide hover:text-text-primary transition-colors"
      >
        {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <History size={14} />
        <span className="flex-1">History</span>
        {instances.length > 0 && (
          <span className="text-[10px] font-normal text-text-muted bg-bg-hover rounded-full px-1.5 py-0.5">
            {instances.length}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="mt-2 space-y-0.5 max-h-48 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-3">
              <div className="animate-spin rounded-full h-3.5 w-3.5 border border-accent border-t-transparent" />
            </div>
          ) : instances.length === 0 ? (
            <p className="text-xs text-text-muted py-1 px-1">No meetings yet</p>
          ) : (
            instances.map((inst) => {
              const isCurrent = inst.id === currentInstanceId;
              const isActive = inst.id === activeDrawerId;
              return (
                <button
                  key={inst.id}
                  onClick={() => !isCurrent && onOpenMeeting(inst.id)}
                  disabled={isCurrent}
                  className={`w-full text-left flex items-start gap-2 px-2 py-1.5 rounded-md text-xs transition-colors ${
                    isCurrent
                      ? 'bg-accent/10 text-accent cursor-default'
                      : isActive
                        ? 'bg-bg-hover text-text-primary ring-1 ring-accent/30'
                        : 'text-text-secondary hover:bg-bg-hover hover:text-text-primary'
                  }`}
                >
                  <Calendar size={11} className="mt-0.5 flex-shrink-0 opacity-60" />
                  <div className="flex-1 min-w-0">
                    <span className="block truncate">
                      {formatShortDate(inst.meeting_date)}
                      {isCurrent && (
                        <span className="ml-1.5 text-[10px] opacity-70">(current)</span>
                      )}
                    </span>
                    {inst.title && (
                      <span className="block text-[10px] text-text-muted truncate">{inst.title}</span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
